import { plural, t } from '@lingui/core/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn, NoddiIcon } from 'noddi-ui-common';
import {
  calendarStatusChipVariants,
  calendarStatusDotVariants,
  NoddiEmptyPage,
  NoddiLink,
  NoddiSheet,
  PrettySlug
} from 'noddi-ui-web';
import {
  CustomDateFormats,
  customerRoutes,
  formatLicensePlate,
  formatPhoneNumber,
  NoddiDateFormats,
  noddiFormatDate
} from 'noddi-util';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CALENDAR_ENTRY_STATUS,
  CALENDAR_STATUS_KEYS,
  CalendarStatusKey,
  FleetCalendarBooking,
  FleetCalendarProposal,
  formatBookingTime,
  getCalendarStatusKey,
  getCalendarStatusLabel
} from '../../utils/fleetCalendarBookingDisplay';
import { CarManagerNames } from '../Common/CarManagerNames';
import { NewBookingButton } from '../Common/NewBookingButton';
import { ApiErrorMessageWithTrans } from '../Error/ApiErrorMessageWithTrans';

interface Props {
  dateString: string | null;
  bookings: FleetCalendarBooking[];
  onClose: () => void;
  pendingProposals?: FleetCalendarProposal[];
  proposalErrorNode?: ReactNode;
  /** Replaces the default "new booking" CTA — e.g. a link on to the full calendar. */
  footer?: ReactNode;
  hideNewBookingCta?: boolean;
}

export const DayOverviewDrawer = ({
  dateString,
  bookings,
  onClose,
  pendingProposals = [],
  proposalErrorNode,
  footer,
  hideNewBookingCta
}: Props) => {
  const open = dateString !== null;
  const date = dateString ? new Date(dateString) : null;
  const headingDate = date ? noddiFormatDate(date, CustomDateFormats.FULL_WEEKDAY_MONTH_AND_YEAR) : '';

  const slots = groupBySlot(bookings);
  const counts = countByStatus(bookings);

  const subtitle =
    bookings.length === 0 ? t`No bookings` : plural(bookings.length, { one: '# booking', other: '# bookings' });

  const todayDateString = noddiFormatDate(new Date(), NoddiDateFormats.API_DATE);
  const isFutureDate = dateString !== null && dateString > todayDateString;
  const showNewBookingCta = isFutureDate && !hideNewBookingCta;

  return (
    <NoddiSheet
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
      side='right'
      title={headingDate}
      description={subtitle}
      closeText={t`Close`}
      footer={footer ?? (showNewBookingCta ? <NewBookingButton className='w-full' /> : undefined)}
    >
      <div className='flex flex-col gap-5 pb-4'>
        {proposalErrorNode}
        {counts.length > 0 && (
          <div className='flex flex-wrap gap-2'>
            {counts.map(({ status, count }) => (
              <div key={status} className={calendarStatusChipVariants({ status: CALENDAR_ENTRY_STATUS[status] })}>
                <span aria-hidden className={calendarStatusDotVariants({ status: CALENDAR_ENTRY_STATUS[status] })} />
                <span className='font-semibold'>{count}</span>
                <span>{getCalendarStatusLabel(status)}</span>
              </div>
            ))}
          </div>
        )}

        {pendingProposals.length > 0 && (
          <div className='flex flex-col gap-3'>
            <h3 className='text-lg font-semibold'>{t`Suggested bookings`}</h3>
            <div className='flex flex-col gap-2'>
              {pendingProposals.map((proposal) => (
                <ProposalLink key={proposal.id} proposal={proposal} />
              ))}
            </div>
          </div>
        )}
        {slots.map(({ slot, items }) => (
          <SlotGroup key={slot} slot={slot} bookings={items} onClose={onClose} />
        ))}

        {bookings.length === 0 && (
          <NoddiEmptyPage className='rounded-xl bg-muted/40' title={t`No bookings on this day`} />
        )}
      </div>
    </NoddiSheet>
  );
};

const ProposalLink = ({ proposal }: { proposal: FleetCalendarProposal }) => {
  const { data: driverData, error: driverError } = noddiAsync.useGet({
    type: URLKeys.getBookingProposalDrivers,
    input: { slug: proposal.slug }
  });
  const item = proposal.bookingProposalItems[0];
  const driver = driverData?.cars.find(({ carId }) => carId === item?.car.id)?.driver;

  return (
    <div className='flex flex-col gap-2'>
      <NoddiLink
        to={customerRoutes.bookingProposalLandingPage.getPath({ slug: proposal.slug })}
        variant='outline'
        className='h-auto w-full justify-between py-3'
        endIcon='ArrowRight'
      >
        {item ? (
          <span className='flex min-w-0 flex-col gap-1 text-left'>
            <span className='font-semibold'>{formatLicensePlate(item.car.licensePlate.number)}</span>
            <span className='truncate text-sm text-muted-foreground'>
              {[item.car.make, item.car.model].filter(Boolean).join(' ')}
            </span>
            {driver && (
              <span className='flex items-center gap-2 text-sm text-muted-foreground'>
                <NoddiIcon name='User' size='small' />
                <span className='truncate'>
                  {driver.name}
                  {driver.phoneNumber ? ` · ${formatPhoneNumber(driver.phoneNumber)}` : ''}
                </span>
              </span>
            )}
          </span>
        ) : (
          t`Book suggested services`
        )}
      </NoddiLink>
      {driverError && <ApiErrorMessageWithTrans error={driverError} />}
    </div>
  );
};

interface SlotGroupProps {
  slot: string;
  bookings: FleetCalendarBooking[];
  onClose: () => void;
}

const SlotGroup = ({ slot, bookings, onClose }: SlotGroupProps) => {
  const totalCars = bookings.reduce((sum, booking) => sum + booking.bookingItems.length, 0);
  const carCountLabel = plural(totalCars, { one: '# car', other: '# cars' });

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center gap-2 text-sm'>
        <NoddiIcon name='Clock' size='small' />
        <span className='font-semibold tabular-nums'>{slot}</span>
        <span className='text-muted-foreground'>· {carCountLabel}</span>
      </div>
      <div className='flex flex-col gap-2'>
        {bookings.map((booking) => (
          <BookingCard key={booking.id} booking={booking} onClose={onClose} />
        ))}
      </div>
    </div>
  );
};

interface BookingCardProps {
  booking: FleetCalendarBooking;
  onClose: () => void;
}

const BookingCard = ({ booking, onClose }: BookingCardProps) => {
  const navigate = useNavigate();
  const status = getCalendarStatusKey(booking.status.value);

  const salesItemNames = Array.from(
    new Set(
      booking.bookingItems.flatMap((item) =>
        item.salesItems.filter((salesItem) => salesItem.isPrimary).map((salesItem) => salesItem.name)
      )
    )
  );
  const heading = salesItemNames.join(', ');

  return (
    <button
      type='button'
      onClick={() => {
        onClose();
        navigate(customerRoutes.bookingInfo.getPath({ slug: booking.slug }));
      }}
      className='flex flex-col gap-2 rounded-xl border bg-white p-3 text-left transition-colors hover:bg-muted/40'
    >
      <div className='flex items-center gap-2'>
        {heading && <span className='text-sm font-semibold'>{heading}</span>}
        {status && (
          <span className={cn(calendarStatusChipVariants({ status: CALENDAR_ENTRY_STATUS[status] }), 'ml-auto')}>
            <span aria-hidden className={calendarStatusDotVariants({ status: CALENDAR_ENTRY_STATUS[status] })} />
            {getCalendarStatusLabel(status)}
          </span>
        )}
      </div>
      <div className='flex flex-col gap-1'>
        {booking.bookingItems.map((item) => (
          <div key={item.id} className='flex flex-col gap-0.5'>
            <div className='flex items-center gap-2 text-sm'>
              <NoddiIcon name='Car' size='small' className='text-muted-foreground' />
              <span className='truncate'>
                {item.car.make} {item.car.model} {formatLicensePlate(item.car.licensePlateNumber)}
              </span>
            </div>
            <CarManagerNames carManagers={item.car.carManagers} />
          </div>
        ))}
      </div>
      <div className='text-[11px] text-muted-foreground'>
        <PrettySlug slug={booking.slug} />
      </div>
    </button>
  );
};

const groupBySlot = (bookings: FleetCalendarBooking[]) => {
  const map = new Map<string, FleetCalendarBooking[]>();
  for (const booking of bookings) {
    const slot = formatBookingTime(booking);
    const list = map.get(slot) ?? [];
    list.push(booking);
    map.set(slot, list);
  }
  return Array.from(map.entries())
    .map(([slot, items]) => ({ slot, items }))
    .sort((a, b) => a.slot.localeCompare(b.slot));
};

/** One entry per status present on the day, in legend order. */
const countByStatus = (bookings: FleetCalendarBooking[]) => {
  const tally = bookings.reduce<Partial<Record<CalendarStatusKey, number>>>((acc, booking) => {
    const key = getCalendarStatusKey(booking.status.value);
    if (!key) {
      return acc;
    }
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});

  return CALENDAR_STATUS_KEYS.filter((status) => (tally[status] ?? 0) > 0).map((status) => ({
    status,
    count: tally[status] as number
  }));
};
